import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Save, Bot, ShieldCheck } from 'lucide-react';
import agentsService, { Agent } from '../../services/agentsService';
import { PageHeader, Spinner, InfoHint, Button } from '../../components/ui';
import '../../styles/theme.css';

/**
 * Agent configuration: the generalization of StageConfig / Parameters / Settings to a single
 * agent's editable knobs — its base prompt, tunable params, and guardrails. Admin or a per-agent
 * "configurer" grant only (enforced server-side). base_prompt is edited as text; params and
 * guardrails as JSON so the surface is agent-agnostic.
 */
const AgentConfig: React.FC = () => {
  const { agentKey = 'doc' } = useParams();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [basePrompt, setBasePrompt] = useState('');
  const [paramsText, setParamsText] = useState('{}');
  const [guardrailsText, setGuardrailsText] = useState('{}');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { agent } = await agentsService.getAgent(agentKey);
      setAgent(agent);
      setBasePrompt(agent.base_prompt || '');
      setParamsText(JSON.stringify(agent.params || {}, null, 2));
      setGuardrailsText(JSON.stringify(agent.guardrails || {}, null, 2));
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Failed to load agent');
    } finally {
      setLoading(false);
    }
  }, [agentKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg('');
    let params: Record<string, any>;
    let guardrails: Record<string, any>;
    try {
      params = JSON.parse(paramsText || '{}');
      guardrails = JSON.parse(guardrailsText || '{}');
    } catch {
      setMsg('Params and guardrails must be valid JSON.'); setSaving(false); return;
    }
    try {
      const { agent } = await agentsService.updateAgentConfig(agentKey,
        { base_prompt: basePrompt, params, guardrails });
      setAgent(agent);
      setMsg('Saved.');
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="mx-card mx-card-pad"><Spinner label="Loading agent…" /></div>;

  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <Link to="/admin/agent-access" className="mx-subtle" style={{ fontSize: 12.5, textDecoration: 'none' }}>
          ← Agent access
        </Link>
      </div>
      <PageHeader title={`Configure: ${agent?.name || agentKey}`}
        subtitle={agent?.persona || 'Base prompt, tunable params, and guardrails for this agent.'} />

      {!agent?.can_configure && (
        <div className="mx-card mx-card-pad mx-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
          You may view this agent but changes require admin or a configurer grant.
        </div>
      )}

      <div className="mx-card mx-card-pad" style={{ maxWidth: 680 }}>
        {msg && <div className="mx-muted" style={{ fontSize: 13, marginBottom: 12 }}>{msg}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Bot size={15} /> Base prompt
              <InfoHint text="The agent's persona / system instructions. Drives how it reads documents and reasons." />
            </label>
            <textarea className="mx-input" rows={6} style={{ fontFamily: 'inherit', resize: 'vertical', marginTop: 6 }}
              value={basePrompt} onChange={(e) => setBasePrompt(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              Params (JSON)
              <InfoHint text="Tunable per-agent knobs, e.g. classify_min_confidence, default_streams." />
            </label>
            <textarea className="mx-input" rows={6} style={{ fontFamily: 'monospace', fontSize: 12.5, resize: 'vertical', marginTop: 6 }}
              value={paramsText} onChange={(e) => setParamsText(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              Guardrails (JSON)
              <InfoHint text="Policy knobs, e.g. require_human_review, allow_review_override, confidential_stays_local." />
            </label>
            <textarea className="mx-input" rows={5} style={{ fontFamily: 'monospace', fontSize: 12.5, resize: 'vertical', marginTop: 6 }}
              value={guardrailsText} onChange={(e) => setGuardrailsText(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <Button icon={Save} variant="primary" onClick={save} disabled={saving || !agent?.can_configure}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="mx-card mx-card-pad mx-subtle" style={{ fontSize: 11.5, marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <ShieldCheck size={14} /> The orchestrator, event bus, and eval-gate are shared across agents; only this config differs.
      </div>
    </div>
  );
};

export default AgentConfig;
